/**
 * KioskResult — check-in result screen (ADR-073 Decision 5).
 *
 * Shows the visitor their badge number and check-in status.
 * Flagged visitors trigger a staff alert (shown but visitor sees a generic
 * "Please wait for assistance" message). Auto-returns to welcome screen.
 */
import { useEffect, useState } from 'react';
import { Box, Center, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Button as ButtonComponent } from '@themes/aegix';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';
import type { VisitDetail, WatchListEntry } from '../../types/VisitorManagementTypes';

interface ResultState {
  visit: VisitDetail;
  badgeNumber: string;
  screeningTriggered: boolean;
  watchListHits: WatchListEntry[];
  sisMatch: boolean;
}

const AUTO_RETURN_SECONDS = 15;

export function KioskResult() {
  usePageTitle('Kiosk');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ResultState | null;
  const [countdown, setCountdown] = useState(AUTO_RETURN_SECONDS);
  const [paused, setPaused] = useState(false);

  // Auto-return to welcome screen
  useEffect(() => {
    if (paused || countdown <= 0) {
      if (countdown <= 0) navigate('/kiosk', { replace: true });
      return;
    }
    if (countdown === 5) {
      setPaused(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, paused, navigate]);

  const handleExtend = () => {
    setCountdown(AUTO_RETURN_SECONDS);
    setPaused(false);
  };
  const handleReturnNow = () => {
    navigate('/kiosk', { replace: true });
  };

  // If no state, redirect back
  if (!state) {
    navigate('/kiosk', { replace: true });
    return null;
  }

  const { visit, badgeNumber, watchListHits } = state;
  const isFlagged = watchListHits.length > 0 || visit.status === 'flagged';

  return (
    <Box
      minH="100vh"
      bg="bg.subtle"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Center maxW="500px" w="100%" px={8}>
        <VStack gap={8} align="center" w="100%">
          {isFlagged ? (
            <>
              <Box color="orange.500">
                <HiExclamationTriangle size={80} />
              </Box>
              <Heading as="h1" size="xl" textAlign="center">
                Please Wait
              </Heading>
              <Text fontSize="lg" textAlign="center" color="text.secondary">
                A staff member will assist you shortly.
              </Text>
            </>
          ) : (
            <>
              <Box color="green.500">
                <HiCheckCircle size={80} />
              </Box>
              <Heading as="h1" size="xl" textAlign="center">
                Welcome, {visit.visitor_detail?.first_name || 'Visitor'}!
              </Heading>
              <Text fontSize="lg" textAlign="center" color="text.secondary">
                You are checked in.
              </Text>
            </>
          )}

          {/* Badge number */}
          <Box
            bg="bg.panel"
            border="2px solid"
            borderColor="border.default"
            borderRadius="lg"
            px={8}
            py={4}
            textAlign="center"
          >
            <Text fontSize="sm" color="text.secondary" mb={1}>
              Badge Number
            </Text>
            <Text fontSize="3xl" fontWeight="bold" fontFamily="mono">
              {badgeNumber}
            </Text>
          </Box>

          {/* Visit details */}
          {!isFlagged && (
            <VStack gap={1} align="center">
              {visit.host_name && (
                <Text fontSize="md" color="text.secondary">
                  Visiting: {visit.host_name}
                </Text>
              )}
              {visit.destination && (
                <Text fontSize="md" color="text.secondary">
                  Destination: {visit.destination}
                </Text>
              )}
            </VStack>
          )}

          {/* Print badge placeholder */}
          {!isFlagged && (
            <Text fontSize="sm" color="text.secondary">
              Please collect your badge from the printer.
            </Text>
          )}

          {/* Return button */}
          <VStack gap={2}>
            {paused ? (
              <Box
                role="alert"
                aria-live="assertive"
                bg="yellow.50"
                _dark={{ bg: 'yellow.900' }}
                border="1px solid"
                borderColor="yellow.500"
                borderRadius="md"
                p={4}
                textAlign="center"
                w="100%"
              >
                <Text fontWeight="600" mb={3}>
                  This screen will return to the welcome page.
                </Text>
                <HStack gap={3} justify="center">
                  <ButtonComponent
                    visual="primary"
                    onClick={handleExtend}
                    minH="44px"
                   data-e2e="stay-on-this-page-button">
                    Stay on this page
                  </ButtonComponent>
                  <ButtonComponent
                    visual="secondary"
                    onClick={handleReturnNow}
                    minH="44px"
                   data-e2e="return-now-button">
                    Return now
                  </ButtonComponent>
                </HStack>
              </Box>
            ) : (
              <>
                <ButtonComponent
                  visual="secondary"
                  onClick={handleReturnNow}
                  minH="44px"
                  minW="44px"
                 data-e2e="done-button">
                  Done
                </ButtonComponent>
                <Text fontSize="xs" color="text.secondary" aria-live="polite">
                  Returning to welcome screen in {countdown}s
                </Text>
              </>
            )}
          </VStack>
        </VStack>
      </Center>
    </Box>
  );
}
